import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Phone, Clock, ShieldCheck, IndianRupee, MapPin, ArrowRight } from "lucide-react";
import { CONTACT, waLink, telLink } from "../lib/execora";

// ─── Categories ────────────────────────────────────────────────────────────
const CATEGORIES = [
  {
    emoji: "🛒",
    name: "Daily Essentials",
    tagline: "Fresh from your neighbourhood",
    chips: ["Grocery run", "Fruits & vegetables", "Breakfast pickup", "Coffee & essentials", "Newspapers"],
    accent: { bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d" },
  },
  {
    emoji: "🎂",
    name: "Food & Bakery",
    tagline: "Hot & fresh, same day",
    chips: ["Cakes & pastries", "Meat & seafood", "Baked items"],
    accent: { bg: "#fdf2f8", border: "#f9a8d4", text: "#be185d" },
  },
  {
    emoji: "🧺",
    name: "Errands & Pickup",
    tagline: "We run the errand, you relax",
    chips: ["Dry cleaning", "Tailor pickup", "Shoe repair", "Key cutting"],
    accent: { bg: "#faf5ff", border: "#d8b4fe", text: "#7e22ce" },
  },
  {
    emoji: "🔧",
    name: "Repairs & Documents",
    tagline: "Tasks you keep postponing",
    chips: ["Appliance repair", "Stamp paper", "Document drop", "Courier & post"],
    accent: { bg: "#f8fafc", border: "#cbd5e1", text: "#334155" },
  },
  {
    emoji: "🌿",
    name: "Lifestyle & Gifts",
    tagline: "Little things that make a day",
    chips: ["Plants & nursery", "Pet supplies", "Gift wrapping", "Stationery"],
    accent: { bg: "#f0fdf9", border: "#6ee7b7", text: "#065f46" },
  },
  {
    emoji: "✨",
    name: "Anything Else",
    tagline: "Just tell us — we'll figure it out",
    chips: ["Any local task", "Custom request"],
    accent: { bg: "#fff1f2", border: "#fecdd3", text: "#be123c" },
  },
];

const WORDS = ["groceries", "bakery", "errands", "repairs", "anything"];
const HOLD_MS = 4500;

// ─── Sub-components ────────────────────────────────────────────────────────
const Badge = ({ icon: Icon, children }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs sm:text-sm text-slate-700 shadow-sm">
    <Icon className="h-3.5 w-3.5 text-[#65a30d]" strokeWidth={2} />
    <span className="font-medium">{children}</span>
  </div>
);

const CyclingWord = () => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % WORDS.length), 2400);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="inline-block relative overflow-hidden" style={{ minWidth: "220px" }}>
      <AnimatePresence mode="wait">
        <motion.span
          key={WORDS[index]}
          initial={{ y: 26, opacity: 0, filter: "blur(4px)" }}
          animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
          exit={{ y: -22, opacity: 0, filter: "blur(4px)" }}
          transition={{ duration: 0.36, ease: [0.16, 1, 0.3, 1] }}
          className="block text-[#84cc15]"
        >
          {WORDS[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

// ─── Category Card ─────────────────────────────────────────────────────────
const CategoryCard = () => {
  const [idx, setIdx] = useState(0);
  const [key, setKey] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % CATEGORIES.length);
      setKey((k) => k + 1);
    }, HOLD_MS);
    return () => clearInterval(t);
  }, []);

  const goTo = (i) => {
    setIdx(i);
    setKey((k) => k + 1);
  };

  const c = CATEGORIES[idx];

  return (
    <div className="relative">
      {/* Ambient glow */}
      <div
        className="absolute -inset-6 bg-gradient-to-tr from-[#84cc15]/15 via-transparent to-slate-900/5 rounded-[2.5rem] blur-2xl pointer-events-none"
        aria-hidden="true"
      />

      {/* Live pill */}
      <motion.div
        animate={{ y: [0, -5, 0] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-4 left-5 z-10 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-xs font-semibold text-slate-800"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-[#84cc15] opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#84cc15]" />
        </span>
        Live in Indiranagar
      </motion.div>

      {/* Card */}
      <div className="relative rounded-[1.75rem] border border-slate-200 shadow-[0_16px_48px_-12px_rgba(15,23,42,0.15)] bg-white overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
            className="p-7"
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-5">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shrink-0"
                style={{ background: c.accent.bg, border: `1.5px solid ${c.accent.border}` }}
              >
                {c.emoji}
              </div>
              <div>
                <div
                  className="text-[11px] font-bold uppercase tracking-widest mb-0.5"
                  style={{ color: c.accent.text }}
                >
                  {c.name}
                </div>
                <div className="text-lg font-semibold text-slate-900 leading-snug">
                  {c.tagline}
                </div>
              </div>
            </div>

            {/* Service chips */}
            <div className="flex flex-wrap gap-2">
              {c.chips.map((chip) => (
                <span
                  key={chip}
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200"
                >
                  {chip}
                </span>
              ))}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-5 border-t border-slate-100">
              <span className="text-xs text-slate-400">From local shops in Indiranagar</span>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Progress bar */}
        <motion.div
          key={`prog-${key}`}
          className="h-0.5 bg-[#84cc15]"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: HOLD_MS / 1000, ease: "linear" }}
          style={{ transformOrigin: "left" }}
        />
      </div>

      {/* Dot nav */}
      <div className="flex justify-center gap-2 mt-4">
        {CATEGORIES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className="transition-all duration-300 rounded-full focus:outline-none"
            style={{
              width: i === idx ? "18px" : "6px",
              height: "6px",
              background: i === idx ? "#84cc15" : "#e2e8f0",
            }}
            aria-label={`Go to ${CATEGORIES[i].name}`}
          />
        ))}
      </div>
    </div>
  );
};

// // ─── Floating WhatsApp FAB ─────────────────────────────────────────────────
// const QUICK_TASKS = [
//   "Order groceries 🛒",
//   "Get medicines 💊",
//   "Book salon visit ✂️",
//   "Send a parcel 📦",
//   "Urgent errand ⚡",
// ];

// const FloatingCTA = () => {
//   const [open, setOpen] = useState(false);
//   return (
//     <div className="fixed bottom-6 right-5 z-50 flex flex-col items-end gap-2">
//       <AnimatePresence>
//         {open && (
//           <motion.div
//             initial={{ opacity: 0, y: 10, scale: 0.95 }}
//             animate={{ opacity: 1, y: 0, scale: 1 }}
//             exit={{ opacity: 0, y: 8, scale: 0.95 }}
//             transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
//             className="bg-white rounded-2xl border border-slate-200 shadow-xl p-3 w-52 mb-1"
//           >
//             <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 px-1">
//               Quick order
//             </p>
//             {QUICK_TASKS.map((task) => (
//               <a
//                 key={task}
//                 href={`https://wa.me/${CONTACT.wa}?text=${encodeURIComponent("Hi! I want to: " + task)}`}
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 className="flex items-center gap-2 w-full px-2 py-2 rounded-xl text-sm text-slate-700 hover:bg-slate-50 transition-colors"
//               >
//                 {task}
//                 <ArrowRight className="h-3.5 w-3.5 ml-auto text-slate-400" strokeWidth={2} />
//               </a>
//             ))}
//           </motion.div>
//         )}
//       </AnimatePresence>
//       <motion.button
//         onClick={() => setOpen((o) => !o)}
//         whileHover={{ scale: 1.06 }}
//         whileTap={{ scale: 0.94 }}
//         className="relative flex items-center justify-center h-14 w-14 rounded-full bg-[#25D366] text-white focus:outline-none"
//         style={{ boxShadow: "0 8px 24px -4px rgba(37,211,102,0.4)" }}
//         aria-label="Quick order on WhatsApp"
//       >
//         <AnimatePresence mode="wait">
//           {open ? (
//             <motion.span
//               key="close"
//               initial={{ rotate: -45, opacity: 0 }}
//               animate={{ rotate: 0, opacity: 1 }}
//               exit={{ rotate: 45, opacity: 0 }}
//               transition={{ duration: 0.2 }}
//             >
//               <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
//                 <path d="M18 6L6 18M6 6l12 12" />
//               </svg>
//             </motion.span>
//           ) : (
//             <motion.span
//               key="wa"
//               initial={{ rotate: 45, opacity: 0 }}
//               animate={{ rotate: 0, opacity: 1 }}
//               exit={{ rotate: -45, opacity: 0 }}
//               transition={{ duration: 0.2 }}
//             >
//               <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
//                 <path d="M19.11 17.24c-.28-.14-1.66-.82-1.92-.92-.26-.1-.45-.14-.64.14s-.73.92-.9 1.11c-.17.19-.33.21-.61.07-.28-.14-1.19-.44-2.27-1.41-.84-.75-1.4-1.67-1.57-1.95-.16-.28-.02-.43.12-.57.13-.13.28-.33.42-.5.14-.17.19-.28.28-.47.1-.19.05-.36-.02-.5-.07-.14-.64-1.54-.87-2.1-.23-.55-.46-.48-.64-.49h-.55c-.19 0-.5.07-.76.36-.26.28-1 .98-1 2.39 0 1.41 1.02 2.78 1.17 2.98.14.19 2.02 3.08 4.9 4.32.68.29 1.22.47 1.64.6.69.22 1.31.19 1.8.12.55-.08 1.66-.68 1.9-1.34.24-.66.24-1.22.17-1.34-.07-.12-.26-.19-.54-.33zM12.05 2C6.5 2 2 6.5 2 12.03c0 1.9.5 3.76 1.46 5.4L2 22l4.71-1.43A9.98 9.98 0 0 0 12.05 22C17.6 22 22.1 17.5 22.1 11.97 22.1 6.44 17.6 2 12.05 2z" />
//               </svg>
//             </motion.span>
//           )}
//         </AnimatePresence>
//         {!open && (
//           <span className="absolute inset-0 rounded-full animate-ping bg-[#25D366] opacity-20" />
//         )}
//       </motion.button>
//     </div>
//   );
// };

// ─── Hero ──────────────────────────────────────────────────────────────────
export const Hero = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const containerVariants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
  };

  return (
    <>
      <section
        id="top"
        data-testid="hero-section"
        ref={ref}
        className="relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-dot opacity-70" aria-hidden="true" />
        <motion.div
          className="absolute -top-32 -right-32 h-[520px] w-[520px] rounded-full bg-[#84cc15]/10 blur-3xl"
          animate={{ scale: [1, 1.08, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 md:pt-20 md:pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">

            {/* Left column */}
            <motion.div
              className="lg:col-span-7"
              variants={containerVariants}
              initial="hidden"
              animate={inView ? "show" : "hidden"}
            >
              <motion.div variants={itemVariants}>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-medium tracking-wide">
                  <MapPin className="h-3.5 w-3.5 text-[#84cc15]" strokeWidth={2.2} />
                  Serving Indiranagar, Bengaluru
                </div>
              </motion.div>

              <motion.h1
                variants={itemVariants}
                className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 tracking-tight leading-[1.08]"
              >
                Get <CyclingWord />
                <span className="block">from your neighbourhood</span>
                <span className="block relative inline-block mt-1">
                  <span className="relative z-10">in minutes</span>
                  <motion.span
                    className="absolute left-0 right-0 bottom-1 h-3 bg-[#84cc15]/55 -z-0 rounded-sm"
                    initial={{ scaleX: 0 }}
                    animate={inView ? { scaleX: 1 } : {}}
                    transition={{ delay: 0.55, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    style={{ transformOrigin: "left" }}
                  />
                </span>
              </motion.h1>

              <motion.p
                variants={itemVariants}
                className="mt-5 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
              >
                We connect you to nearby shops and services so you can order anything and get it at your doorstep, fast.
              </motion.p>

              <motion.div variants={itemVariants} className="mt-7 flex flex-wrap items-center gap-3">
                <motion.a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-whatsapp-cta"
                  whileHover={{ scale: 1.03, boxShadow: "0 8px 24px -4px rgba(132,204,21,0.45)" }}
                  whileTap={{ scale: 0.97 }}
                  className="group inline-flex items-center gap-2 px-5 py-3.5 rounded-xl text-base font-medium text-white bg-[#84cc15] hover:bg-[#65a30d] shadow-sm transition-colors"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                    <path d="M19.11 17.24c-.28-.14-1.66-.82-1.92-.92-.26-.1-.45-.14-.64.14s-.73.92-.9 1.11c-.17.19-.33.21-.61.07-.28-.14-1.19-.44-2.27-1.41-.84-.75-1.4-1.67-1.57-1.95-.16-.28-.02-.43.12-.57.13-.13.28-.33.42-.5.14-.17.19-.28.28-.47.1-.19.05-.36-.02-.5-.07-.14-.64-1.54-.87-2.1-.23-.55-.46-.48-.64-.49h-.55c-.19 0-.5.07-.76.36-.26.28-1 .98-1 2.39 0 1.41 1.02 2.78 1.17 2.98.14.19 2.02 3.08 4.9 4.32.68.29 1.22.47 1.64.6.69.22 1.31.19 1.8.12.55-.08 1.66-.68 1.9-1.34.24-.66.24-1.22.17-1.34-.07-.12-.26-.19-.54-.33zM12.05 2C6.5 2 2 6.5 2 12.03c0 1.9.5 3.76 1.46 5.4L2 22l4.71-1.43A9.98 9.98 0 0 0 12.05 22C17.6 22 22.1 17.5 22.1 11.97 22.1 6.44 17.6 2 12.05 2z" />
                  </svg>
                  Order on WhatsApp
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                </motion.a>
                <motion.a
                  href={telLink()}
                  data-testid="hero-call-cta"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center gap-2 px-5 py-3.5 rounded-xl text-base font-medium text-slate-900 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
                >
                  <Phone className="h-5 w-5" strokeWidth={2} />
                  Call now
                </motion.a>
              </motion.div>

              <motion.div variants={itemVariants} className="mt-8 flex flex-wrap gap-2.5">
                <Badge icon={Clock}>≤ 90 min delivery</Badge>
                <Badge icon={IndianRupee}>Orders up to ₹5,000</Badge>
                <Badge icon={ShieldCheck}>Verified local vendors</Badge>
              </motion.div>

              <motion.p
                variants={itemVariants}
                className="mt-6 text-xs text-slate-500"
                data-testid="hero-phone-note"
              >
                Prefer the phone? Dial {CONTACT.callDisplay}. We pick up. We run.
              </motion.p>
            </motion.div>

            {/* Right column */}
            <motion.div
              className="hidden lg:block lg:col-span-5"
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <CategoryCard />
            </motion.div>

          </div>
        </div>
      </section>
{/* 
      <FloatingCTA /> */}
    </>
  );
};

export default Hero;
